import type { TimeEntryItem } from '@hygge/shared';
import { dayMonth } from './reopen-window';

/**
 * The sentence shown on an entry the server would refuse to change.
 *
 * The decision itself is never taken here — `canEdit` comes from the server,
 * which is the only place that knows about project membership, the monthly
 * cutoff and import provenance. This turns its `canEditReason` code into words,
 * so the row can explain itself instead of opening a dialog that ends in an
 * error toast. [HW-864]
 *
 * That includes WHEN a month locks — 06:00 UTC on the 1st, on the server's
 * clock. A second copy here would read the viewer's clock, which is the one
 * thing the cutoff never trusts. [HW-916]
 */
export function entryLockMessage(
  entry: Pick<TimeEntryItem, 'canEditReason' | 'startedAt' | 'source' | 'reopenedFrom'>,
): string | null {
  switch (entry.canEditReason ?? null) {
    case 'not_yours':
      return 'Only its owner, a manager of the project, HR or a role holding "Edit any time log" can change this entry';
    case 'no_permission':
      return 'Your role does not include editing time entries';
    case 'imported':
      // Tempo and Paper rows come through the MCP endpoint, which has no run
      // to undo and no edit tool, so they cannot be changed in the CRM at all.
      if (entry.source === 'tempo') return 'Imported from Jira Tempo — it cannot be changed here';
      if (entry.source === 'paper') return 'Tracked in Paper — it cannot be changed here';
      return entry.source === 'clockify'
        ? 'Imported from Clockify — undo the import run to change it'
        : 'Imported from an external system — undo the import run to change it';
    case 'closed_month':
      // A reopened period of the reader's on this project that starts after
      // this day: say so, beside My Time's notice that it is open. [HW-963]
      return entry.reopenedFrom
        ? `${entryMonthLabel(entry.startedAt)} is closed to edits — your reopened period on this project starts on ${dayMonth(entry.reopenedFrom)}`
        : `${entryMonthLabel(entry.startedAt)} is closed to edits — ask the project manager or HR to make the correction`;
    default:
      return null;
  }
}

/**
 * "August 2026" for the month the cutoff froze.
 *
 * UTC, deliberately: the server's cutoff counts UTC months — it asks which UTC
 * month `started_at` falls in — so the month named on screen has to be the
 * month the server refused, read the same way. Under the wall-time model every
 * Time Tracker date reads the entry's own digits, which are UTC digits, so this
 * no longer stands apart from the rest; it is spelled out here because it is
 * the one that reports a server rule rather than describing when the work
 * happened, and a host-zone read would name the next month for the last
 * evenings east of UTC. [HW-916]
 */
function entryMonthLabel(startedAt: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(startedAt));
}

/** Whether the screen may offer an edit at all. Absent flag ⇒ no. [HW-864] */
export function canEditEntry(entry: Pick<TimeEntryItem, 'canEdit'>): boolean {
  return entry.canEdit === true;
}

/**
 * Whether the screen may offer a delete. Read apart from `canEdit` since
 * HW-972: "Edit any time log" and "Delete any time log" are granted
 * independently, so a row can open to the pencil and not to the trash. Absent
 * flag ⇒ no, as for the edit.
 */
export function canDeleteEntry(entry: Pick<TimeEntryItem, 'canDelete'>): boolean {
  return entry.canDelete === true;
}

/**
 * Whether the server would accept a copy of this entry on the same date. A
 * create, so neither "any time log" grant widens it: a holder's own
 * closed-month row may open to the pencil and still refuse its Duplicate, and
 * a task's row on an archived or on-hold project refuses it to everyone (a
 * task-less copy may still be moved to an active project). The shape rules —
 * past midnight, under a minute — stay `canDuplicateEntry`'s. Absent flag ⇒
 * no. [HW-972]
 */
export function serverAcceptsCopy(entry: Pick<TimeEntryItem, 'canDuplicate'>): boolean {
  return entry.canDuplicate === true;
}

/**
 * What a Reports → Show entries row offers, every control from a server flag:
 * the pencil and the Logged cell from `canEdit`, the trash from `canDelete`,
 * the Billable cell from `canEdit` AND "Edit any time log" — the one grant the
 * server lets change Billable Hours (a PATCH with `billableDuration` from
 * anyone else is a 403). A row that carries no billable figure has nothing to
 * edit there. `lockTitle` is the same sentence the timesheet shows on a row it
 * will not open: it explains a missing pencil and editable cells, and is null
 * while the pencil is live. Here rather than in the component because vitest is
 * node-env in this repo. [HW-974]
 */
export function reportRowControls(
  entry: Pick<
    TimeEntryItem,
    'canEdit' | 'canDelete' | 'canEditReason' | 'startedAt' | 'source' | 'billableDuration'
  >,
  holdsEditAll: boolean,
): { pencil: boolean; trash: boolean; loggedCell: boolean; billableCell: boolean; lockTitle: string | null } {
  const editable = canEditEntry(entry);
  return {
    pencil: editable,
    trash: canDeleteEntry(entry),
    loggedCell: editable,
    billableCell: editable && holdsEditAll && entry.billableDuration !== undefined,
    lockTitle: entryLockMessage(entry),
  };
}
